import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useData } from '../../contexts/DataContext';
import GoodsReceiptWizard from './GoodsReceiptWizard';
import QuickReceiptDialog from './QuickReceiptDialog';
import ReceiptDetailDialog from './ReceiptDetailDialog';
import { 
  Package, Plus, Search, Eye, Download, FileSpreadsheet, 
  RefreshCw, Filter, Calendar, Building, Zap, Edit
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import * as XLSX from 'xlsx';
import { SupplierAutocomplete } from '../../components/ui/supplier-autocomplete';

const ReceiptsHistoryPage = () => {
  const { plants, selectedPlant } = useData();
  const [receipts, setReceipts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [showQuickReceipt, setShowQuickReceipt] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  
  // Use separate state for filter values to prevent object recreation issues
  const [supplierId, setSupplierId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: 50 };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      if (supplierId) params.supplier_id = supplierId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get('/wms/receipts', { params });
      const data = response.data;
      if (data.items) {
        setReceipts(data.items);
        setTotalPages(data.total_pages || 1);
        setTotalItems(data.total || 0);
      } else {
        setReceipts(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Eroare la incarcare receptii');
    } finally {
      setLoading(false);
    }
  }, [selectedPlant, supplierId, dateFrom, dateTo, page]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Real-time: refresh on stock changes
  useRealtimeEvent('stock_change', useCallback(() => { fetchData(); }, [fetchData]));

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlant, page]);

  const handleExportExcel = () => {
    try {
      const exportData = receipts.map(r => ({
        'Nr. Receptie': r.receipt_number,
        'Nr. Factura': r.invoice_number,
        'Data Factura': new Date(r.invoice_date).toLocaleDateString('ro-RO'),
        'Furnizor': r.supplier_name,
        'Sediu': r.plant_name,
        'Articole': r.items_count,
        'Cantitate Totala': r.total_quantity,
        'Valoare': `${r.total_invoice_value.toFixed(2)} ${r.currency}`,
        'Primit de': r.received_by_name,
        'Data Primire': new Date(r.created_at).toLocaleDateString('ro-RO')
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Receptii');
      XLSX.writeFile(wb, `receptii_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export Excel generat!');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  const clearFilters = () => {
    setSupplierId('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const handleSupplierSelect = useCallback((supplier) => {
    setSupplierId(supplier ? supplier.id : '');
    setPage(1);
  }, []);

  const handleEditReceipt = (receipt) => {
    setEditingReceipt(receipt);
    if (receipt.receipt_type === 'quick') {
      setShowQuickReceipt(true);
    } else {
      setShowWizard(true);
    }
  };

  return (
    <div className="pb-20 lg:pb-6">
      {/* Header */}
      <div className="sticky top-0 bg-slate-50 z-10 pb-4 pt-2 border-b mb-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Istoric Receptii</h1>
            <p className="text-base text-slate-600 mt-1">Gestionare receptii marfa si intrari in stoc</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} className="h-12">
              <RefreshCw className="w-5 h-5 mr-2" />
              <span className="hidden sm:inline">Reincarca</span>
            </Button>
            <Button variant="outline" onClick={handleExportExcel} className="h-12">
              <FileSpreadsheet className="w-5 h-5 mr-2" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button onClick={() => setShowQuickReceipt(true)} variant="outline" className="h-12 bg-orange-50 border-orange-300">
              <Zap className="w-5 h-5 mr-2" />
              Receptie Rapida
            </Button>
            <Button onClick={() => setShowWizard(true)} className="h-12 bg-blue-600">
              <Plus className="w-5 h-5 mr-2" />
              Receptie cu Factura
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Furnizor</Label>
                <SupplierAutocomplete
                  suppliers={suppliers}
                  value={supplierId || null}
                  onSelect={handleSupplierSelect}
                  placeholder="Cauta furnizor..."
                  triggerClassName="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label>De la data</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Pana la data</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-11" />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchData} className="w-full h-11">
                  <Filter className="w-4 h-4 mr-2" />
                  Aplica Filtre
                </Button>
              </div>
            </div>
            {(supplierId || dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-2">
                Reseteaza Filtre
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Receipts List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : receipts.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <Package className="w-20 h-20 text-slate-300 mx-auto mb-4" />
              <p className="text-xl font-semibold text-slate-700">Nicio receptie</p>
              <p className="text-slate-500 mt-2">Creeaza prima receptie folosind butoanele de mai sus</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {receipts.map((receipt) => (
              <Card key={receipt.id} className="hover:shadow-lg transition-shadow border-2">
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-xl font-bold text-slate-900">{receipt.receipt_number}</h3>
                        <Badge className="bg-green-600 text-white">Confirmat</Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">Nr. Factura</p>
                          <p className="font-semibold">{receipt.invoice_number || '—'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Furnizor</p>
                          <p className="font-semibold">{receipt.supplier_name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Cantitate</p>
                          <p className="font-semibold">{receipt.total_quantity} {receipt.items_count > 1 ? `(${receipt.items_count} articole)` : receipt.items?.[0]?.unit || 'buc'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Valoare</p>
                          {receipt.total_invoice_value > 0 ? (
                            <p className="font-semibold text-green-600">{receipt.total_invoice_value.toFixed(2)} {receipt.currency}</p>
                          ) : (
                            <p className="font-semibold text-slate-400">—</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                        <span>📅 {new Date(receipt.invoice_date).toLocaleDateString('ro-RO')}</span>
                        <span>👤 {receipt.received_by_name}</span>
                        <span>🏭 {receipt.plant_name}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => { setSelectedReceipt(receipt); setShowDetailDialog(true); }} className="h-12">
                        <Eye className="w-5 h-5 mr-2" />
                        Detalii
                      </Button>
                      <Button variant="outline" onClick={() => handleEditReceipt(receipt)} className="h-12">
                        <Edit className="w-5 h-5 mr-2" />
                        Editeaza
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-2">
            <span className="text-sm text-slate-500">
              Pagina {page} din {totalPages} ({totalItems} receptii)
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="btn-prev-page">Anterior</Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page + i - 2;
                if (p < 1 || p > totalPages) return null;
                return <Button key={p} variant={p === page ? "default" : "outline"} size="sm" onClick={() => setPage(p)} className="w-9">{p}</Button>;
              })}
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="btn-next-page">Urmator</Button>
            </div>
          </div>
        )}
      </div>

      {/* Wizards and Dialogs */}
      <GoodsReceiptWizard
        open={showWizard}
        onClose={() => { setShowWizard(false); setEditingReceipt(null); }}
        onSuccess={fetchData}
        editReceipt={editingReceipt}
      />
      
      <QuickReceiptDialog
        open={showQuickReceipt}
        onClose={() => { setShowQuickReceipt(false); setEditingReceipt(null); }}
        onSuccess={fetchData}
        editReceipt={editingReceipt}
      />
      
      <ReceiptDetailDialog
        open={showDetailDialog}
        onClose={() => setShowDetailDialog(false)}
        receipt={selectedReceipt}
      />
    </div>
  );
};

export default ReceiptsHistoryPage;
